// One column registry for the whole tile: the on-screen table and the CSV
// exporter both read from here, so a column can never mean one thing in the
// table and another in the file.
//
// `type` drives both rendering and CSV formatting. `accessor` is the field on
// an enriched row as the server spells it.

export const COLUMNS = [
  { id: 'orderNumber',            label: 'Order Number',            type: 'text',     table: true,  export: true,  width: 'w-40' },
  { id: 'status',                 label: 'Status',                  type: 'status',   table: true,  export: true,  width: 'w-32' },
  { id: 'voidedBy',               label: 'Voided By',               type: 'text',     table: true,  export: true,  width: 'w-36' },
  { id: 'shipper',                label: 'Shipper',                 type: 'text',     table: false, export: true,  width: 'w-36' },
  { id: 'masterTrackingNumber',   label: 'Master Tracking Number',  type: 'tracking', table: true,  export: true,  width: 'w-52' },
  { id: 'carrier',                label: 'Carrier',                 type: 'text',     table: true,  export: true,  width: 'w-24' },
  { id: 'processDate',            label: 'Process Date',            type: 'datetime', table: true,  export: true,  width: 'w-44' },
  { id: 'shipDate',               label: 'Ship Date',               type: 'date',     table: true,  export: true,  width: 'w-32' },
  { id: 'voidedDate',             label: 'Voided Date',             type: 'datetime', table: true,  export: true,  width: 'w-44' },
  { id: 'reshipped',              label: 'Reshipped',               type: 'bool',     table: true,  export: true,  width: 'w-28' },
  { id: 'reshipDuration',         label: 'Time to Reship',          type: 'text',     table: true,  export: true,  width: 'w-32' },
  { id: 'secondsToReship',        label: 'Seconds to Reship',       type: 'number',   table: false, export: false, width: 'w-32' },
  { id: 'replacementTracking',    label: 'Replacement Tracking',    type: 'tracking', table: false, export: true,  width: 'w-52' },
  { id: 'replacementShipDate',    label: 'Replacement Ship Date',   type: 'date',     table: false, export: false, width: 'w-32' },
  { id: 'voidsOnOrder',           label: 'Voids on Order',          type: 'number',   table: false, export: false, width: 'w-28' },
  { id: 'outsideRequestedRange',  label: 'Ship Date Outside Range', type: 'bool',     table: false, export: false, width: 'w-40' },
];

export const COLUMN_BY_ID = COLUMNS.reduce((acc, c) => { acc[c.id] = c; return acc; }, {});

export const DEFAULT_TABLE_COLUMNS = COLUMNS.filter((c) => c.table).map((c) => c.id);
export const DEFAULT_EXPORT_COLUMNS = COLUMNS.filter((c) => c.export).map((c) => c.id);

// ---------------------------------------------------------------------------
// Date handling
//
// Upstream timestamps carry no timezone ("2026-09-15T09:24:13.337") and are
// warehouse local time. They are split with a regex and reassembled as text —
// never passed through new Date(), which would read them as the browser's zone
// and shift every displayed value.
// ---------------------------------------------------------------------------

export const parseStamp = (value) => {
  if (!value || typeof value !== 'string') return null;
  const m = value.match(/^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2})(?::(\d{2}))?)?/);
  if (!m) return null;
  return {
    year: m[1], month: m[2], day: m[3],
    hour: m[4] || null, minute: m[5] || null, second: m[6] || '00',
  };
};

export const DATE_FORMATS = [
  { id: 'iso',        label: 'ISO — 2026-09-15 09:24:13' },
  { id: 'iso_t',      label: 'ISO with T — 2026-09-15T09:24:13' },
  { id: 'us',         label: 'US 12-hour — 9/15/2026 9:24 AM' },
  { id: 'us24',       label: 'US 24-hour — 9/15/2026 09:24' },
  { id: 'date_only',  label: 'Date only — 2026-09-15' },
  { id: 'us_date',    label: 'US date only — 9/15/2026' },
  { id: 'raw',        label: 'Raw — exactly as the API sent it' },
];

/**
 * Format a timestamp string. `dateOnly` columns drop the time even when the
 * chosen format carries one, because ShipDate is always midnight and printing
 * "00:00:00" on every row is noise.
 */
export const formatStamp = (value, format = 'iso', { dateOnly = false } = {}) => {
  if (value === null || value === undefined || value === '') return '';
  if (format === 'raw') return String(value);

  const p = parseStamp(value);
  if (!p) return String(value);

  const ymd = `${p.year}-${p.month}-${p.day}`;
  const usDate = `${Number(p.month)}/${Number(p.day)}/${p.year}`;

  if (dateOnly || format === 'date_only') return format === 'us_date' || format === 'us' || format === 'us24' ? usDate : ymd;
  if (format === 'us_date') return usDate;

  const hasTime = p.hour !== null;
  if (!hasTime) return format.startsWith('us') ? usDate : ymd;

  const hms = `${p.hour}:${p.minute}:${p.second}`;

  switch (format) {
    case 'iso_t':  return `${ymd}T${hms}`;
    case 'us24':   return `${usDate} ${p.hour}:${p.minute}`;
    case 'us': {
      const h = Number(p.hour);
      const suffix = h >= 12 ? 'PM' : 'AM';
      const h12 = h % 12 === 0 ? 12 : h % 12;
      return `${usDate} ${h12}:${p.minute} ${suffix}`;
    }
    case 'iso':
    default:       return `${ymd} ${hms}`;
  }
};

export const BOOL_FORMATS = [
  { id: 'yes_no',      label: 'Yes / No' },
  { id: 'true_false',  label: 'TRUE / FALSE' },
  { id: 'one_zero',    label: '1 / 0' },
  { id: 'y_n',         label: 'Y / N' },
];

export const formatBool = (value, format = 'yes_no') => {
  if (value === null || value === undefined) return '';
  switch (format) {
    case 'true_false': return value ? 'TRUE' : 'FALSE';
    case 'one_zero':   return value ? '1' : '0';
    case 'y_n':        return value ? 'Y' : 'N';
    case 'yes_no':
    default:           return value ? 'Yes' : 'No';
  }
};

export const STATUS_FORMATS = [
  { id: 'label', label: 'Readable — Voided / Shipped Replacement' },
  { id: 'code',  label: 'Raw code — V / S' },
  { id: 'both',  label: 'Both — V (Voided)' },
];

export const formatStatus = (row, format = 'label') => {
  const code = row.status || '';
  const label = row.statusLabel || code;
  switch (format) {
    case 'code': return code;
    case 'both': return code ? `${code} (${label})` : '';
    case 'label':
    default:     return label;
  }
};
